import {
    actionS1BaseImages,
    actionS1FlexImages,
    actionS2BaseImages,
    actionS2FlexImages,
    actionS3BaseImages,
    actionS3FlexImages,
    actionStarterImages,
    cellarActionS1Images,
    cellarActionS2Images,
    cellarActionS3Images,
    hiveActionS1Images,
    hiveActionS2Images,
    hiveActionS3Images,
    seedEventActionImages
} from "./images/cards/cardsLists";

export type ActionCardSeason = 1 | 2 | 3;
export type ActionCardSource = "starter" | "base" | "advanced" | "hive" | "cellar" | "seed-event";

export interface ActionCardDefinition {
    name: string;
    imageUrl: string;
    season: ActionCardSeason | null;
    source: ActionCardSource;
    code?: string;
}

function toCardName(imageUrl: string): string {
    return imageUrl.split("/").pop()?.split(".")[0] ?? imageUrl;
}

function buildDefinitions(images: string[], season: ActionCardSeason | null, source: ActionCardSource): ActionCardDefinition[] {
    return images.map((imageUrl) => ({
        name: toCardName(imageUrl),
        imageUrl,
        season,
        source
    }));
}

export const STARTER_ACTION_CARDS = buildDefinitions(actionStarterImages, null, "starter");
export const BASE_ACTION_CARDS = [
    ...buildDefinitions(actionS1BaseImages, 1, "base"),
    ...buildDefinitions(actionS2BaseImages, 2, "base"),
    ...buildDefinitions(actionS3BaseImages, 3, "base")
];
export const ADVANCED_ACTION_CARDS = [
    ...buildDefinitions(actionS1FlexImages, 1, "advanced"),
    ...buildDefinitions(actionS2FlexImages, 2, "advanced"),
    ...buildDefinitions(actionS3FlexImages, 3, "advanced")
];
export const HIVE_ACTION_CARDS = [
    ...buildDefinitions(hiveActionS1Images, 1, "hive"),
    ...buildDefinitions(hiveActionS2Images, 2, "hive"),
    ...buildDefinitions(hiveActionS3Images, 3, "hive")
];
export const CELLAR_ACTION_CARDS = [
    ...buildDefinitions(cellarActionS1Images, 1, "cellar"),
    ...buildDefinitions(cellarActionS2Images, 2, "cellar"),
    ...buildDefinitions(cellarActionS3Images, 3, "cellar")
];
export const SEED_EVENT_ACTION_CARDS = buildDefinitions(seedEventActionImages, null, "seed-event");

export const ALL_ACTION_CARDS = [
    ...STARTER_ACTION_CARDS,
    ...BASE_ACTION_CARDS,
    ...ADVANCED_ACTION_CARDS,
    ...HIVE_ACTION_CARDS,
    ...CELLAR_ACTION_CARDS,
    ...SEED_EVENT_ACTION_CARDS
];

const actionCardLookup = new Map(ALL_ACTION_CARDS.map((card) => [card.name, card] as const));
const compactAlphabet = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
const compactCodeWidth = 2;
const compactSelectableCards = [
    ...BASE_ACTION_CARDS,
    ...ADVANCED_ACTION_CARDS,
    ...CELLAR_ACTION_CARDS
].map((card, index) => ({
    ...card,
    code: encodeCompactNumber(index)
}));
const compactCardCodeLookup = new Map(compactSelectableCards.map((card) => [card.code!, card.name] as const));
const compactCardNameLookup = new Map(compactSelectableCards.map((card) => [card.name, card.code!] as const));

export function getActionCardDefinition(name: string): ActionCardDefinition | undefined {
    return actionCardLookup.get(name);
}

export function getCellarDraftOfferPool(): ActionCardDefinition[] {
    return CELLAR_ACTION_CARDS.slice();
}

export function getCellarCampaignDraftPool(): ActionCardDefinition[] {
    return [
        ...BASE_ACTION_CARDS,
        ...ADVANCED_ACTION_CARDS,
        ...CELLAR_ACTION_CARDS
    ];
}

export function encodeSelectedActionCards(cardNames: string[]): string {
    return cardNames
        .map((cardName) => compactCardNameLookup.get(cardName))
        .filter((code): code is string => code != null)
        .sort()
        .join("");
}

export function decodeSelectedActionCards(encoded: string): string[] {
    const selectedCardNames: string[] = [];

    for (let index = 0; index < encoded.length; index += compactCodeWidth) {
        const code = encoded.slice(index, index + compactCodeWidth);

        if (code.length < compactCodeWidth) {
            continue;
        }

        const cardName = compactCardCodeLookup.get(code);

        if (cardName != null) {
            selectedCardNames.push(cardName);
        }
    }

    return selectedCardNames;
}

function encodeCompactNumber(value: number): string {
    let remaining = value;
    let encoded = "";

    do {
        encoded = compactAlphabet[remaining % compactAlphabet.length] + encoded;
        remaining = Math.floor(remaining / compactAlphabet.length);
    } while (remaining > 0);

    return encoded.padStart(compactCodeWidth, "0");
}
